import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { asId } from '@shared/brand';
import { getServices } from '@infrastructure/services';
import { useAreaFocus } from '../providers/AreaFocusProvider';
import { selectClass } from '../design-system';
import styles from './AreaFocusPicker.module.css';

/**
 * Qué área están mirando las pantallas de gestión.
 *
 * Sólo aparece para quien tiene más de una —`global` y `super`—; para los otros
 * cuatro roles no hay nada que elegir y un desplegable con una sola opción es
 * ruido que además insinúa que hay más.
 *
 * **Vive en la barra superior y no dentro de cada pantalla.** El foco es uno para
 * calendario, reportes, personas y bitácora: repetir el control en cada una
 * invita a que dos digan cosas distintas, y entonces nadie sabe de qué área es lo
 * que tiene delante. Aquí arriba está siempre a la vista, que es justo lo que
 * hace falta cuando lo que se mira no es lo propio.
 */
export const AreaFocusPicker = (): ReactNode => {
  const focus = useAreaFocus();

  const areas = useQuery({
    queryKey: ['organization', 'areas'],
    staleTime: 5 * 60 * 1000,
    enabled: focus?.canChange === true,
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  if (focus === null || !focus.canChange) return null;

  /* Las inactivas siguen listadas si es la enfocada: quitarla de golpe dejaría
     el desplegable señalando a otra área sin que nadie lo tocara. */
  const list = (areas.data ?? []).filter(
    (area) => area.active || area.id === focus.areaId,
  );

  return (
    <label className={styles.wrap}>
      <span className={styles.label}>Área</span>
      <select
        className={selectClass()}
        aria-label="Área que estás mirando"
        value={focus.areaId}
        onChange={(event) => {
          focus.setAreaId(asId<'AreaId'>(event.target.value));
        }}
      >
        {list.map((area) => (
          <option key={area.id} value={area.id}>
            {area.name}
            {area.id === focus.ownAreaId ? ' · la tuya' : ''}
          </option>
        ))}
      </select>
    </label>
  );
};
